import type { AlignmentView, CoupleView, DeckCard, MatchView, PendingFeedback, Provider, SearchResult, SeenEntry, SwipeDirection, WishlistEntry } from "@hyggebam/shared";
import { DEFAULT_THEME, JUSTWATCH_ATTRIBUTION, SUPPORTED_LANGUAGES } from "@hyggebam/shared";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "./api.ts";
import { DetailsOverlay } from "./components/DetailsOverlay.tsx";
import { SwipeCard } from "./components/SwipeCard.tsx";
import { BrandLogo } from "./components/BrandLogo.tsx";
import { ThemePicker } from "./components/ThemePicker.tsx";
import { ThemeScreen } from "./components/ThemeScreen.tsx";
import { clearFeedbackSeen, clearLastCouple, clearSeat, getFeedbackSeen, getLastCouple, getSeat, navigate, setFeedbackSeen, setLastCouple, setSeat, tokenFromPath } from "./store.ts";
import { applyTheme, clearStoredTheme, getStoredTheme, storeTheme } from "./theme.ts";

const THEME_PICKED_KEY = "hyggebam.themePicked";

const COUNTRIES = [
  ["DK", "🇩🇰 Denmark"],
  ["US", "🇺🇸 United States"],
  ["GB", "🇬🇧 United Kingdom"],
  ["DE", "🇩🇪 Germany"],
  ["CH", "🇨🇭 Switzerland"],
  ["SE", "🇸🇪 Sweden"],
  ["NO", "🇳🇴 Norway"],
  ["FR", "🇫🇷 France"],
  ["ES", "🇪🇸 Spain"],
] as const;

export function App() {
  const [token, setToken] = useState<string | null>(tokenFromPath());

  useEffect(() => {
    const onPop = () => setToken(tokenFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Auto-login: if no token in path but we have one saved, redirect.
  useEffect(() => {
    if (!tokenFromPath()) {
      const saved = getLastCouple();
      if (saved) navigate(`/c/${saved}`);
    }
  }, []);

  return <div className="app">{token ? <CoupleApp token={token} /> : <CreateScreen />}</div>;
}

// ── Create couple (§2 step 1–2) ──────────────────────────────────────────────
function CreateScreen() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [country, setCountry] = useState("DK");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function create() {
    setBusy(true);
    setErr("");
    try {
      const view = await api.createCouple([a, b], country);
      const tok = view.share_url.split("/c/")[1]!;
      setLastCouple(tok);
      navigate(`/c/${tok}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <div>
        <div className="logo">hygge밤</div>
        <h1>Find a movie, together.</h1>
        <p className="sub">
          Two of you, one cosy night in. Add your names, swipe a few films, and we'll surface what you both want to
          watch — on the services you already pay for.
        </p>
      </div>
      <div>
        <label className="field">
          <span>Your name</span>
          <input data-testid="create-name-0" value={a} onChange={(e) => setA(e.target.value)} placeholder="Alex" maxLength={24} />
        </label>
        <label className="field">
          <span>Their name</span>
          <input data-testid="create-name-1" value={b} onChange={(e) => setB(e.target.value)} placeholder="Sam" maxLength={24} />
        </label>
        <label className="field">
          <span>Where are you watching from?</span>
          <select data-testid="create-country" value={country} onChange={(e) => setCountry(e.target.value)}>
            {COUNTRIES.map(([c, label]) => (
              <option key={c} value={c}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div>
        <div className="error">{err}</div>
        <button data-testid="create-btn" className="btn" disabled={busy || !a.trim() || !b.trim()} onClick={create}>
          {busy ? "Creating…" : "Create couple"}
        </button>
        <p className="attribution">No account, no login. The link is your key — keep it between you two.</p>
      </div>
    </div>
  );
}

// ── Couple shell: loads state, routes between screens ────────────────────────
type Screen = "loading" | "notfound" | "seat" | "setup" | "feedback" | "deck" | "matches" | "settings" | "wishlist" | "battle" | "seen" | "theme" | "theme-picker";

function CoupleApp({ token }: { token: string }) {
  const [couple, setCouple] = useState<CoupleView | null>(null);
  const [pending, setPending] = useState<PendingFeedback | null>(null);
  const [seat, setSeatState] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>("loading");
  const [feedbackDone, setFeedbackDone] = useState(false);
  // Just picked a match to watch → land on the deck, don't immediately nag with
  // "did you watch it?" (the prompt belongs on the NEXT open, not right after).
  const justChoseRef = useRef(false);
  const [detailsTmdbId, setDetailsTmdbId] = useState<number | null>(null);
  // Tracks the last couple theme_id we applied so we don't refetch on every poll
  const lastAppliedCoupleThemeRef = useRef<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const { couple, pending_feedback } = await api.open(token);
      setCouple(couple);
      setPending(pending_feedback);
      setLastCouple(token);
      const mySeat = getSeat(couple.id);
      setSeatState(mySeat);
      const justChose = justChoseRef.current;
      justChoseRef.current = false;
      if (!mySeat) setScreen("seat");
      else if (couple.services.length === 0) setScreen("setup");
      else if (pending_feedback && !feedbackDone && !justChose && getFeedbackSeen(couple.id) !== pending_feedback.tmdb_id) {
        // Ask once, on a genuine open. Mark it seen immediately so a hard reload
        // lands in the app instead of re-hijacking the whole screen with it
        // (it stays resolvable from the swipe/details). `justChose` skips the
        // self-trigger right after picking a match to watch.
        setFeedbackSeen(couple.id, pending_feedback.tmdb_id);
        setScreen("feedback");
      } else setScreen((s) => {
        if (s === "loading" || s === "seat" || s === "setup") {
          // Show theme picker for first-timers who haven't picked a theme
          const themePicked = localStorage.getItem(THEME_PICKED_KEY);
          if (!themePicked && !getStoredTheme()) return "theme-picker";
          return "deck";
        }
        return s;
      });
    } catch (e) {
      clearLastCouple();
      setScreen(e instanceof ApiError && e.status === 404 ? "notfound" : "notfound");
    }
  }, [token, feedbackDone]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Couple-theme live poll (runs every ~4s while mounted).
  // Precedence: per-device localStorage theme > couple.theme_id > DEFAULT_THEME.
  // Only fetches/applies the couple theme when no local override exists and the
  // couple theme_id has changed from what we last applied.
  useEffect(() => {
    const id = setInterval(async () => {
      // If the user has a local theme override, respect it — do nothing.
      if (getStoredTheme()) return;
      try {
        const { couple: fresh } = await api.open(token);
        setCouple((prev) => {
          if (!prev) return fresh;
          if (prev.theme_id !== fresh.theme_id) return fresh;
          return prev;
        });
        const tid = fresh.theme_id;
        if (tid && tid !== lastAppliedCoupleThemeRef.current) {
          const theme = await api.getTheme(token, tid);
          applyTheme(theme);
          lastAppliedCoupleThemeRef.current = tid;
        } else if (!tid && lastAppliedCoupleThemeRef.current) {
          // Couple theme was cleared by a partner
          applyTheme(DEFAULT_THEME);
          lastAppliedCoupleThemeRef.current = null;
        }
      } catch {
        // ignore poll errors
      }
    }, 4000);
    return () => clearInterval(id);
  }, [token]);

  // On initial load: if no local theme but couple has one, apply it once.
  useEffect(() => {
    if (!couple) return;
    if (getStoredTheme()) return; // local wins
    const tid = couple.theme_id;
    if (!tid) return;
    if (lastAppliedCoupleThemeRef.current === tid) return;
    api.getTheme(token, tid)
      .then((theme) => {
        applyTheme(theme);
        lastAppliedCoupleThemeRef.current = tid;
      })
      .catch(() => {});
  }, [couple, token]);

  if (screen === "loading") return <div className="center"><div className="spinner" /></div>;
  if (screen === "notfound") return <NotFound />;
  if (!couple) return <div className="center"><div className="spinner" /></div>;

  const me = couple.seats.find((s) => s.person_id === seat) ?? null;

  if (screen === "seat")
    return (
      <SeatScreen
        couple={couple}
        onPick={(pid) => {
          setSeat(couple.id, pid);
          setSeatState(pid);
          reload();
        }}
      />
    );

  if (screen === "setup" && seat)
    return <SetupScreen token={token} couple={couple} seat={seat} onDone={reload} />;

  if (screen === "feedback" && pending && seat)
    return (
      <FeedbackScreen
        token={token}
        pending={pending}
        seat={seat}
        onResolved={() => {
          setFeedbackDone(true);
          setScreen("deck");
        }}
      />
    );

  const switchSeat = () => {
    clearSeat(couple.id);
    setSeatState(null);
    setScreen("seat");
  };

  const nav = (
    <TopBar
      screen={screen}
      onNav={(s) => {
        setScreen(s);
      }}
    />
  );

  if (screen === "matches") return <>{nav}<MatchesScreen token={token} coupleId={couple.id} seat={seat ?? ""} onChoose={() => { justChoseRef.current = true; reload(); }} onDetails={setDetailsTmdbId} /></>;
  if (screen === "settings")
    return (
      <>
        {nav}
        <SettingsScreen
          token={token}
          couple={couple}
          seat={seat ?? ""}
          onCoupleChange={(c) => setCouple(c)}
          onGoTheme={() => setScreen("theme-picker")}
          onSwitchSeat={switchSeat}
          onLeave={() => {
            clearLastCouple();
            if (seat) clearSeat(couple.id);
            navigate("/");
          }}
        />
      </>
    );
  // ── Theme Picker (new primary theme entry) ──────────────────────────────────
  if (screen === "theme-picker") {
    const isOnboarding = !localStorage.getItem(THEME_PICKED_KEY) && !getStoredTheme();
    function markPickedAndDeck() {
      localStorage.setItem(THEME_PICKED_KEY, "1");
      setScreen("deck");
    }
    return (
      <>
        {!isOnboarding && nav}
        <ThemePicker
          onPickPreset={() => markPickedAndDeck()}
          onCustom={() => setScreen("theme")}
          onSkip={markPickedAndDeck}
          showSkip={isOnboarding}
        />
      </>
    );
  }

  // ── AI-art Theme studio (primary custom flow: flux art + palette) ────────────
  if (screen === "theme")
    return (
      <>
        {nav}
        <ThemeScreen
          token={token}
          seat={seat ?? ""}
          coupleThemeId={couple.theme_id}
          onCoupleChange={(newThemeId) => setCouple((c) => c ? { ...c, theme_id: newThemeId } : c)}
          onApplied={() => {
            // First-run: applying a theme completes onboarding → straight to the deck.
            // Returning users stay in the studio (so they can also "Set for couple").
            const wasOnboarding = !localStorage.getItem(THEME_PICKED_KEY);
            localStorage.setItem(THEME_PICKED_KEY, "1");
            if (wasOnboarding) setScreen("deck");
          }}
        />
      </>
    );
  if (screen === "wishlist" && seat)
    return (
      <>
        {nav}
        <WishlistScreen
          token={token}
          seat={seat}
          couple={couple}
          onStartBattle={() => setScreen("battle")}
          onDetails={setDetailsTmdbId}
        />
        {detailsTmdbId !== null && (
          <DetailsOverlay
            token={token}
            seat={seat}
            tmdbId={detailsTmdbId}
            onClose={() => setDetailsTmdbId(null)}
          />
        )}
      </>
    );
  if (screen === "battle" && seat)
    return (
      <>
        {nav}
        <div className="battle-banner" data-testid="battle-banner">
          <span>⚔️ Wishlist battle</span>
          <button className="btn ghost" style={{ width: "auto", padding: "6px 14px", fontSize: 13 }} onClick={() => setScreen("wishlist")}>
            Back
          </button>
        </div>
        <DeckScreen token={token} seat={seat} source="wishlist" onGoMatches={() => setScreen("matches")} onDetails={setDetailsTmdbId} />
      </>
    );
  if (screen === "seen" && seat)
    return (
      <>
        {nav}
        <SeenItScreen token={token} seat={seat} onDetails={setDetailsTmdbId} />
        {detailsTmdbId !== null && (
          <DetailsOverlay
            token={token}
            seat={seat}
            tmdbId={detailsTmdbId}
            onClose={() => setDetailsTmdbId(null)}
          />
        )}
      </>
    );

  return (
    <>
      {nav}
      <DeckScreen token={token} seat={seat!} source="all" onGoMatches={() => setScreen("matches")} onCoupleChange={(c) => setCouple(c)} couple={couple} onDetails={setDetailsTmdbId} />
      {detailsTmdbId !== null && (
        <DetailsOverlay
          token={token}
          seat={seat!}
          tmdbId={detailsTmdbId}
          onClose={() => setDetailsTmdbId(null)}
        />
      )}
    </>
  );
}

const NAV_ITEMS: { testId: string; label: string; icon: string; screens: Screen[] }[] = [
  { testId: "nav-deck",     label: "Swipe",    icon: "🃏", screens: ["deck", "battle"] },
  { testId: "nav-wishlist", label: "Wishlist", icon: "🔖", screens: ["wishlist"] },
  { testId: "nav-matches",  label: "Matches",  icon: "✨", screens: ["matches"] },
  { testId: "nav-seen",     label: "Seen",     icon: "👁", screens: ["seen"] },
];

function TopBar({
  screen,
  onNav,
}: {
  screen: Screen;
  onNav: (s: Screen) => void;
}) {
  const onSettings = screen === "settings";
  return (
    <header className="topbar">
      {/* Logo mark doubles as the Settings entry — no separate gear.
          Sits left of the nav pills, matched in height. */}
      <button
        type="button"
        data-testid="nav-settings"
        className={`brand${onSettings ? " active" : ""}`}
        onClick={() => onNav("settings")}
        aria-current={onSettings ? "page" : undefined}
        aria-label="Settings"
      >
        <BrandLogo className="brand-logo" />
      </button>
      <nav className="topbar-scroll" aria-label="Main navigation">
        {NAV_ITEMS.map((item) => {
          const active = item.screens.includes(screen);
          const targetScreen = item.screens[0] as Screen;
          return (
            <button
              key={item.testId}
              data-testid={item.testId}
              className={`navpill${active ? " active" : ""}`}
              onClick={() => onNav(targetScreen)}
              aria-current={active ? "page" : undefined}
            >
              <span className="navpill-icon" aria-hidden="true">{item.icon}</span>
              <span className="navpill-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </header>
  );
}

// ── Seat pick (§2 step 3) ────────────────────────────────────────────────────
function SeatScreen({ couple, onPick }: { couple: CoupleView; onPick: (pid: string) => void }) {
  return (
    <div className="center">
      <div>
        <div className="logo">hygge밤</div>
        <h1>Who are you?</h1>
        <p className="sub">Pick your seat. You can switch on any device — there's no owner here.</p>
      </div>
      <div className="seatlist" data-testid="seat-list">
        {couple.seats.map((s) => (
          <button key={s.person_id} data-testid="seat-button" onClick={() => onPick(s.person_id)}>
            {s.display_name}
            <span className="av">{s.display_name.slice(0, 1).toUpperCase()}</span>
          </button>
        ))}
      </div>
      <ShareInline token={couple.share_url.split("/c/")[1]!} couple={couple} />
    </div>
  );
}

// ── Setup: country, services, optional import (§2 steps 4–5) ─────────────────
function SetupScreen({
  token,
  couple,
  seat,
  onDone,
}: {
  token: string;
  couple: CoupleView;
  seat: string;
  onDone: () => void;
}) {
  const [country, setCountry] = useState(couple.current_country);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(couple.services));
  const [busy, setBusy] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [importSource, setImportSource] = useState<"imdb_ratings" | "imdb_watchlist">("imdb_ratings");

  const loadProviders = useCallback(async () => {
    setProviders(await api.providers(token));
  }, [token]);
  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  async function changeCountry(c: string) {
    setCountry(c);
    await api.setCountry(token, c);
    await loadProviders();
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function uploadCsv(file: File) {
    setImportMsg("Reading…");
    try {
      const text = await file.text();
      const { job_id } = await api.importCsv(token, seat, importSource, text);
      setImportMsg("Importing your taste…");
      // Poll the background job.
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 600));
        const s = await api.importStatus(token, job_id);
        if (s.status === "done") {
          setImportMsg(`Imported ${s.stats.rows_matched} titles ✓`);
          return;
        }
        if (s.status === "failed") {
          setImportMsg("Import failed");
          return;
        }
      }
      setImportMsg("Still importing in the background…");
    } catch {
      setImportMsg("Couldn't read that file");
    }
  }

  async function start() {
    setBusy(true);
    await api.setServices(token, [...selected]);
    onDone();
  }

  return (
    <div className="scroll">
      <div className="logo" style={{ marginBottom: 8 }}>
        hygge밤
      </div>
      <h1>Set the scene</h1>
      <label className="field">
        <span>Where are you watching from?</span>
        <select value={country} onChange={(e) => changeCountry(e.target.value)}>
          {COUNTRIES.map(([c, label]) => (
            <option key={c} value={c}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>What can you watch on? (subscriptions you share)</span>
      </label>
      <div className="svcgrid" data-testid="services-grid">
        {providers.slice(0, 10).map((p) => (
          <div key={p.provider_id} data-testid="service-option" data-provider-id={p.provider_id} className={`svc-opt ${selected.has(p.provider_id) ? "on" : ""}`} onClick={() => toggle(p.provider_id)}>
            {p.name}
          </div>
        ))}
      </div>

      <label className="field" style={{ marginTop: 22 }}>
        <span>Warm-start your taste (optional) — upload an IMDb CSV export</span>
      </label>
      <div className="svcgrid" style={{ marginBottom: 10 }}>
        <div className={`svc-opt ${importSource === "imdb_ratings" ? "on" : ""}`} onClick={() => setImportSource("imdb_ratings")}>
          Ratings export
        </div>
        <div className={`svc-opt ${importSource === "imdb_watchlist" ? "on" : ""}`} onClick={() => setImportSource("imdb_watchlist")}>
          Watchlist export
        </div>
      </div>
      <button className="btn secondary" onClick={() => fileRef.current?.click()}>
        Choose CSV file
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])}
      />
      {importMsg && <p className="sub" style={{ marginTop: 8 }}>{importMsg}</p>}

      <div style={{ height: 18 }} />
      <button data-testid="start-swiping" className="btn" disabled={busy || selected.size === 0} onClick={start}>
        {busy ? "Warming up…" : selected.size === 0 ? "Pick at least one service" : "Start swiping →"}
      </button>
      <div style={{ height: 16 }} />
    </div>
  );
}

// ── Settings screen (unified hub) ────────────────────────────────────────────
function SettingsScreen({
  token,
  couple,
  seat,
  onCoupleChange,
  onGoTheme,
  onSwitchSeat,
  onLeave,
}: {
  token: string;
  couple: CoupleView;
  seat: string;
  onCoupleChange: (c: CoupleView) => void;
  onGoTheme: () => void;
  onSwitchSeat: () => void;
  onLeave: () => void;
}) {
  const me = couple.seats.find((s) => s.person_id === seat);

  // ── Section 1: You ──
  const [displayName, setDisplayName] = useState(me?.display_name ?? "");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState("");
  const [importMsg, setImportMsg] = useState("");
  const [importSource, setImportSource] = useState<"imdb_ratings" | "imdb_watchlist">("imdb_ratings");
  const importFileRef = useRef<HTMLInputElement>(null);

  // ── Section 2: Couple ──
  const [country, setCountry] = useState(couple.current_country);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(couple.services));
  const [coldMsg, setColdMsg] = useState("");
  const [savingServices, setSavingServices] = useState(false);
  const [language, setLanguage] = useState(couple.preferred_language);

  // ── Section 3: Share ──
  const [confirmRegen, setConfirmRegen] = useState(false);

  // ── Section 4: Theme ──
  const [themeMsg, setThemeMsg] = useState("");

  // Keep local display name in sync when couple data refreshes
  useEffect(() => {
    const freshName = couple.seats.find((s) => s.person_id === seat)?.display_name ?? "";
    setDisplayName(freshName);
  }, [couple, seat]);

  // Live sync: poll api.open every 4s to catch partner changes
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { couple: fresh } = await api.open(token);
        setCountry((prev) => {
          if (prev !== fresh.current_country) {
            loadProviders(fresh.current_country);
            return fresh.current_country;
          }
          return prev;
        });
        setSelected((prev) => {
          const freshSet = new Set(fresh.services);
          const same =
            prev.size === freshSet.size && [...prev].every((v) => freshSet.has(v));
          if (!same) {
            onCoupleChange(fresh);
            return freshSet;
          }
          return prev;
        });
      } catch {
        // ignore poll errors silently
      }
    }, 4000);
    return () => clearInterval(id);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProviders = useCallback(async (forCountry?: string) => {
    const list = await api.providers(token);
    setProviders(list);
    if (forCountry !== undefined) setCountry(forCountry);
  }, [token]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // ── You: rename ──
  async function saveName() {
    if (!displayName.trim() || displayName.trim() === me?.display_name) return;
    setNameSaving(true);
    setNameMsg("");
    try {
      const updated = await api.rename(token, seat, displayName.trim());
      onCoupleChange(updated);
      setNameMsg("Name saved!");
      setTimeout(() => setNameMsg(""), 2500);
    } catch {
      setNameMsg("Couldn't save name");
      setTimeout(() => setNameMsg(""), 2500);
    } finally {
      setNameSaving(false);
    }
  }

  // ── You: IMDb import ──
  async function uploadCsv(file: File) {
    setImportMsg("Reading…");
    try {
      const text = await file.text();
      const { job_id } = await api.importCsv(token, seat, importSource, text);
      setImportMsg("Importing your taste…");
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 600));
        const s = await api.importStatus(token, job_id);
        if (s.status === "done") {
          setImportMsg(`Imported ${s.stats.rows_matched} titles`);
          return;
        }
        if (s.status === "failed") {
          setImportMsg("Import failed");
          return;
        }
      }
      setImportMsg("Still importing in the background…");
    } catch {
      setImportMsg("Couldn't read that file");
    }
  }

  // ── Couple: country / services ──
  async function changeCountry(c: string) {
    setCountry(c);
    setColdMsg("Updating your region…");
    try {
      const { wasCold, view } = await api.setCountry(token, c);
      onCoupleChange(view);
      await loadProviders(c);
      if (wasCold) {
        setColdMsg("Region updated — fetching availability…");
        setTimeout(() => setColdMsg(""), 3000);
      } else {
        setColdMsg("");
      }
    } catch {
      setColdMsg("Could not update region");
      setTimeout(() => setColdMsg(""), 3000);
    }
  }

  async function changeLanguage(lang: string) {
    setLanguage(lang);
    try {
      onCoupleChange(await api.setLanguage(token, lang));
    } catch {
      /* keep the optimistic selection; next poll reconciles */
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function saveServices() {
    setSavingServices(true);
    try {
      const view = await api.setServices(token, [...selected]);
      onCoupleChange(view);
    } finally {
      setSavingServices(false);
    }
  }

  // ── Theme helpers ──
  const storedTheme = getStoredTheme();
  const currentThemeName = storedTheme
    ? storedTheme.name
    : couple.theme_id
    ? "Couple's shared theme"
    : "Hygge (default)";

  async function setCoupleTheme() {
    const local = getStoredTheme();
    if (!local) return;
    setThemeMsg("Setting…");
    try {
      const updated = await api.setCoupleTheme(token, local.id);
      onCoupleChange(updated);
      setThemeMsg("Set for both partners!");
      setTimeout(() => setThemeMsg(""), 3000);
    } catch {
      setThemeMsg("Couldn't update couple theme.");
      setTimeout(() => setThemeMsg(""), 3000);
    }
  }

  async function copyCoupleThenome() {
    if (!couple.theme_id) return;
    setThemeMsg("Loading…");
    try {
      const theme = await api.getTheme(token, couple.theme_id);
      applyTheme(theme);
      storeTheme(theme);
      setThemeMsg("Couple's theme copied to this device!");
      setTimeout(() => setThemeMsg(""), 3000);
    } catch {
      setThemeMsg("Couldn't load couple theme.");
      setTimeout(() => setThemeMsg(""), 3000);
    }
  }

  async function resetTheme() {
    clearStoredTheme();
    applyTheme(DEFAULT_THEME);
    setThemeMsg("Reset to default.");
    setTimeout(() => setThemeMsg(""), 2500);
  }

  const sectionStyle: React.CSSProperties = {
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  };
  const sectionHeadStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--muted)",
    marginBottom: 14,
  };

  return (
    <div className="scroll">
      <h1>Settings</h1>

      {/* ── Section 1: You ── */}
      <div style={sectionStyle}>
        <div style={sectionHeadStyle}>You</div>

        <label className="field">
          <span>Your display name</span>
          <input
            data-testid="settings-name-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={24}
            onKeyDown={(e) => e.key === "Enter" && saveName()}
          />
        </label>
        <button
          data-testid="settings-name-save"
          className="btn secondary"
          style={{ marginTop: 4 }}
          disabled={nameSaving || !displayName.trim() || displayName.trim() === me?.display_name}
          onClick={saveName}
        >
          {nameSaving ? "Saving…" : "Save name"}
        </button>
        {nameMsg && (
          <p className="sub" style={{ marginTop: 6, color: nameMsg.startsWith("Couldn't") ? "var(--never)" : "var(--like)" }}>
            {nameMsg}
          </p>
        )}

        <div style={{ height: 16 }} />

        <button
          data-testid="switch-seat"
          className="btn ghost"
          style={{ textAlign: "left", color: "var(--muted)", fontSize: 14 }}
          onClick={onSwitchSeat}
        >
          Switch seat — I'm someone else
        </button>

        <div style={{ height: 20 }} />

        <label className="field">
          <span style={{ fontWeight: 700 }}>Upload your IMDb ratings</span>
          <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
            Warm-start your taste with an IMDb CSV export
          </span>
        </label>
        <div className="svcgrid" style={{ marginBottom: 10 }}>
          <div
            className={`svc-opt ${importSource === "imdb_ratings" ? "on" : ""}`}
            onClick={() => setImportSource("imdb_ratings")}
          >
            Ratings export
          </div>
          <div
            className={`svc-opt ${importSource === "imdb_watchlist" ? "on" : ""}`}
            onClick={() => setImportSource("imdb_watchlist")}
          >
            Watchlist export
          </div>
        </div>
        <button className="btn secondary" onClick={() => importFileRef.current?.click()}>
          Choose CSV file
        </button>
        <input
          ref={importFileRef}
          data-testid="import-csv-input"
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])}
        />
        {importMsg && (
          <p
            className="sub"
            data-testid="import-status"
            style={{ marginTop: 8, color: importMsg.startsWith("Imported") ? "var(--like)" : "var(--muted)" }}
          >
            {importMsg}
          </p>
        )}
      </div>

      {/* ── Section 2: Couple ── */}
      <div style={sectionStyle}>
        <div style={sectionHeadStyle}>Couple</div>

        <label className="field">
          <span>Where are you watching from?</span>
          <select
            data-testid="settings-country"
            value={country}
            onChange={(e) => changeCountry(e.target.value)}
          >
            {COUNTRIES.map(([c, label]) => (
              <option key={c} value={c}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {coldMsg && (
          <p className="sub" style={{ marginTop: -8, marginBottom: 10, color: "var(--accent)" }}>
            {coldMsg}
          </p>
        )}

        <label className="field" style={{ marginTop: 12 }}>
          <span>Overview language</span>
          <span style={{ display: "block", fontSize: 12, color: "var(--muted)", margin: "2px 0 6px" }}>
            Movie plots are translated into this language
          </span>
          <select
            data-testid="settings-language"
            value={language}
            onChange={(e) => changeLanguage(e.target.value)}
          >
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field" style={{ marginTop: 12 }}>
          <span style={{ fontWeight: 700 }}>Streaming services</span>
          <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
            Subscriptions you share as a couple
          </span>
        </label>
        <div className="svcgrid" data-testid="settings-services-grid">
          {providers.slice(0, 10).map((p) => (
            <div
              key={p.provider_id}
              data-testid="settings-service"
              data-provider-id={p.provider_id}
              className={`svc-opt ${selected.has(p.provider_id) ? "on" : ""}`}
              onClick={() => toggle(p.provider_id)}
            >
              {p.name}
            </div>
          ))}
        </div>
        <div style={{ height: 12 }} />
        <button className="btn" disabled={savingServices} onClick={saveServices}>
          {savingServices ? "Saving…" : "Save services"}
        </button>
        <div style={{ height: 8 }} />
        <p className="attribution" style={{ marginTop: 4 }}>{JUSTWATCH_ATTRIBUTION}</p>
      </div>

      {/* ── Section 3: Share ── */}
      <div style={sectionStyle}>
        <div style={sectionHeadStyle}>Share</div>
        <p className="sub" style={{ marginBottom: 14 }}>
          Anyone with this link is in. It's your key — don't post it publicly.
        </p>
        <ShareInline token={token} couple={couple} />
        <div style={{ height: 16 }} />
        <p className="sub">If the link ever leaks, you can regenerate it. The old link stops working immediately.</p>
        <div style={{ height: 10 }} />
        {confirmRegen ? (
          <button
            data-testid="confirm-regen-btn"
            className="btn secondary"
            onClick={async () => {
              const v = await api.regenerate(token);
              navigate(`/c/${v.share_url.split("/c/")[1]}`);
              setConfirmRegen(false);
            }}
          >
            Confirm — regenerate link
          </button>
        ) : (
          <button data-testid="regen-btn" className="btn ghost" onClick={() => setConfirmRegen(true)}>
            Regenerate link
          </button>
        )}
      </div>

      {/* ── Section 4: Theme ── */}
      <div style={sectionStyle}>
        <div style={sectionHeadStyle}>Theme</div>
        <p className="sub" style={{ marginBottom: 6 }}>
          Active theme: <strong style={{ color: "var(--ink)" }}>{currentThemeName}</strong>
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
          <button
            data-testid="settings-theme-studio"
            className="btn"
            onClick={onGoTheme}
          >
            Open theme studio
          </button>
          <button
            data-testid="settings-theme-set-couple"
            className="btn secondary"
            disabled={!storedTheme}
            onClick={setCoupleTheme}
          >
            Set my theme for the couple
          </button>
          {couple.theme_id && (
            <button
              data-testid="settings-theme-copy-couple"
              className="btn secondary"
              onClick={copyCoupleThenome}
            >
              Copy couple's theme to my device
            </button>
          )}
          <button
            data-testid="settings-theme-reset"
            className="btn ghost"
            onClick={resetTheme}
          >
            Reset to default
          </button>
        </div>
        {themeMsg && (
          <p className="sub" style={{ marginTop: 10, color: themeMsg.startsWith("Couldn't") ? "var(--never)" : "var(--like)" }}>
            {themeMsg}
          </p>
        )}
      </div>

      {/* ── Section 5: Leave couple ── */}
      <div style={{ paddingTop: 4, paddingBottom: 24 }}>
        <div style={sectionHeadStyle}>Danger zone</div>
        <button
          data-testid="leave-couple"
          className="btn ghost"
          style={{ color: "var(--never)", opacity: 0.8 }}
          onClick={onLeave}
        >
          Leave couple
        </button>
        <div style={{ height: 8 }} />
      </div>
    </div>
  );
}

// ── Wishlist screen + search + battle (Feature 2) ────────────────────────────
function WishlistScreen({
  token,
  seat,
  couple,
  onStartBattle,
  onDetails,
}: {
  token: string;
  seat: string;
  couple: CoupleView;
  onStartBattle: () => void;
  onDetails?: (tmdbId: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [wishlist, setWishlist] = useState<WishlistEntry[]>([]);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load initial wishlist
  useEffect(() => {
    api.wishlist(token).then((r) => setWishlist(r.items)).catch(() => {});
  }, [token]);

  // Live sync: poll wishlist every 4s (Feature 3)
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { items } = await api.wishlist(token);
        setWishlist(items);
      } catch {
        // ignore poll errors
      }
    }, 4000);
    return () => clearInterval(id);
  }, [token]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { results } = await api.search(token, query.trim());
        setResults(results);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, token]);

  async function addToWishlist(tmdbId: number) {
    setAddingId(tmdbId);
    try {
      const { items } = await api.addWishlist(token, seat, tmdbId);
      setWishlist(items);
      // Update in_wishlist on results
      setResults((prev) => prev.map((r) => r.tmdb_id === tmdbId ? { ...r, in_wishlist: true } : r));
    } finally {
      setAddingId(null);
    }
  }

  async function removeFromWishlist(tmdbId: number) {
    setRemovingId(tmdbId);
    try {
      const { items } = await api.removeWishlist(token, tmdbId);
      setWishlist(items);
      // Update in_wishlist on results
      setResults((prev) => prev.map((r) => r.tmdb_id === tmdbId ? { ...r, in_wishlist: false } : r));
    } finally {
      setRemovingId(null);
    }
  }

  const seatName = (personId: string) =>
    couple.seats.find((s) => s.person_id === personId)?.display_name ?? personId;

  return (
    <div className="scroll">
      <h1>Wishlist</h1>

      {/* Search */}
      <label className="field">
        <span>Search for a title to add</span>
        <input
          data-testid="wishlist-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Inception, Parasite…"
        />
      </label>

      {searching && <div className="spinner" style={{ margin: "8px auto" }} />}

      {results.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {results.map((r) => (
            <div
              key={r.tmdb_id}
              data-testid="search-result"
              data-tmdb-id={r.tmdb_id}
              className="matchrow"
            >
              <div
                className="thumb"
                style={{ ...(r.poster ? { backgroundImage: `url(${r.poster})` } : {}), cursor: onDetails ? "pointer" : "default" }}
                onClick={() => onDetails?.(r.tmdb_id)}
              >
                {!r.poster && "🎬"}
              </div>
              <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(r.tmdb_id)}>
                <div className="t">{r.title}</div>
                <div className="m">
                  {[r.year, r.genres.slice(0, 2).join(", ")].filter(Boolean).join(" · ")}
                </div>
              </div>
              {r.in_wishlist ? (
                <button
                  data-testid="remove-wishlist"
                  className="btn secondary"
                  style={{ width: "auto", padding: "8px 12px", fontSize: 12 }}
                  disabled={removingId === r.tmdb_id}
                  onClick={() => removeFromWishlist(r.tmdb_id)}
                >
                  {removingId === r.tmdb_id ? "…" : "Remove"}
                </button>
              ) : (
                <button
                  data-testid="add-wishlist"
                  className="btn"
                  style={{ width: "auto", padding: "8px 12px", fontSize: 12 }}
                  disabled={addingId === r.tmdb_id}
                  onClick={() => addToWishlist(r.tmdb_id)}
                >
                  {addingId === r.tmdb_id ? "…" : "Add"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Couple wishlist */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Your wishlist ({wishlist.length})</h2>
        {wishlist.length >= 1 && (
          <button
            data-testid="start-battle"
            className="btn"
            style={{ width: "auto", padding: "10px 16px", fontSize: 14 }}
            onClick={onStartBattle}
          >
            ⚔️ Start battle
          </button>
        )}
      </div>

      {wishlist.length === 0 && !query && (
        <div style={{ textAlign: "center", padding: "30px 0" }}>
          <div style={{ fontSize: 40 }}>🎯</div>
          <p className="sub" style={{ marginTop: 8 }}>
            Your wishlist is empty. Search above and add titles you both want to see!
          </p>
        </div>
      )}

      {wishlist.map((item) => (
        <div
          key={item.tmdb_id}
          data-testid="wishlist-item"
          data-tmdb-id={item.tmdb_id}
          className="matchrow"
        >
          <div
            className="thumb"
            style={{ ...(item.poster ? { backgroundImage: `url(${item.poster})` } : {}), cursor: onDetails ? "pointer" : "default" }}
            onClick={() => onDetails?.(item.tmdb_id)}
          >
            {!item.poster && "🎬"}
          </div>
          <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(item.tmdb_id)}>
            <div className="t">{item.title}</div>
            <div className="m">
              {[item.year, item.genres.slice(0, 2).join(", ")].filter(Boolean).join(" · ")}
            </div>
            <div className="m" style={{ color: "var(--accent)", fontSize: 11 }}>
              Added by {seatName(item.added_by)}
            </div>
            <div className="chips">
              {item.available_on.map((o) => (
                <span className="chip svc" key={o.service}>
                  {o.name}
                </span>
              ))}
            </div>
          </div>
          <button
            data-testid="remove-wishlist"
            className="btn ghost"
            style={{ width: "auto", padding: "8px 12px", fontSize: 12 }}
            disabled={removingId === item.tmdb_id}
            onClick={() => removeFromWishlist(item.tmdb_id)}
          >
            {removingId === item.tmdb_id ? "…" : "Remove"}
          </button>
        </div>
      ))}

      <p className="attribution">{JUSTWATCH_ATTRIBUTION}</p>
    </div>
  );
}

// ── Deck / swiping (§2 step 6) ───────────────────────────────────────────────
function DeckScreen({
  token,
  seat,
  source = "all",
  onGoMatches,
  onCoupleChange,
  couple,
  onDetails,
}: {
  token: string;
  seat: string;
  source?: "all" | "wishlist";
  onGoMatches: () => void;
  onCoupleChange?: (c: CoupleView) => void;
  couple?: CoupleView;
  onDetails?: (tmdbId: number) => void;
}) {
  const [cards, setCards] = useState<DeckCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [bufferReady, setBufferReady] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [toast, setToast] = useState<{ text: string; undo?: DeckCard } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Quick "how was it?" rating prompt after a deck swipe-down "seen it".
  // Stays until the user picks a reaction (incl. "i don't know") — no auto-dismiss.
  const [seenRate, setSeenRate] = useState<DeckCard | null>(null);
  // Track last-seen couple services/country for live sync
  const lastServicesRef = useRef<string>(JSON.stringify(couple?.services ?? []));
  const lastCountryRef = useRef<string>(couple?.current_country ?? "");
  // Session dedup: track every tmdb_id shown this session so we never repeat.
  const seenIdsRef = useRef<Set<number>>(new Set());
  // Presence: is the partner also on the deck right now? Purely an indicator —
  // the deck stays fully async, there is no separate co-swipe mode.
  const [partnerName, setPartnerName] = useState<string | null>(null);
  // Reset seen set and first-load flag when source changes.
  const prevSourceRef = useRef(source);
  const isFirstLoadRef = useRef(true);
  if (prevSourceRef.current !== source) {
    seenIdsRef.current = new Set();
    isFirstLoadRef.current = true;
    prevSourceRef.current = source;
  }

  const refill = useCallback(async () => {
    const isFirst = isFirstLoadRef.current;
    isFirstLoadRef.current = false;
    const { cards: fetched } = await api.deck(token, seat, 15, source);
    setCards((prev) => {
      const existingIds = new Set(prev.map((c) => c.tmdb_id));
      const novel = fetched.filter(
        (c) => !seenIdsRef.current.has(c.tmdb_id) && !existingIds.has(c.tmdb_id),
      );
      // On first load replace entirely; on refill append novel cards only.
      return isFirst ? novel : [...prev, ...novel];
    });
    setLoading(false);
  }, [token, seat, source]);

  const refreshMatches = useCallback(async () => {
    const m = await api.matches(token);
    setBufferReady(m.ready);
    setMatchCount(m.matches.length);
  }, [token]);

  useEffect(() => {
    refill();
    refreshMatches();
  }, [refill, refreshMatches]);

  // Live sync: poll api.open every 4s; if services/country changed, refetch deck (Feature 3)
  useEffect(() => {
    if (source !== "all") return; // battle mode doesn't need live sync
    const id = setInterval(async () => {
      try {
        const { couple: fresh } = await api.open(token);
        const freshServicesKey = JSON.stringify(fresh.services);
        const freshCountry = fresh.current_country;
        const changed =
          lastServicesRef.current !== freshServicesKey ||
          lastCountryRef.current !== freshCountry;
        if (changed) {
          lastServicesRef.current = freshServicesKey;
          lastCountryRef.current = freshCountry;
          onCoupleChange?.(fresh);
          await refill();
        }
      } catch {
        // ignore poll errors
      }
    }, 4000);
    return () => clearInterval(id);
  }, [token, source, refill, onCoupleChange]);

  // Presence poll: stamps our heartbeat and reads who else is on the deck. Shows
  // a small "partner is swiping too" indicator — the deck never changes mode.
  useEffect(() => {
    if (source !== "all") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { presence } = await api.presence(token, seat);
        if (cancelled) return;
        const partner = presence.find((p) => p.person_id !== seat && p.online);
        setPartnerName(partner ? partner.display_name : null);
      } catch {
        // ignore poll errors
      }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, seat, source]);

  function showToast(text: string, undo?: DeckCard) {
    setToast({ text, undo });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }

  function promptSeenRating(card: DeckCard) {
    setSeenRate(card);
  }

  // Quick reaction → 1–10 rating ("i don't know" leaves it rating-less). The
  // swipe already marked it watched; this just attaches the rating.
  async function rateSeen(card: DeckCard, rating: number | null) {
    setSeenRate(null);
    if (rating != null) await api.markWatched(token, seat, card.tmdb_id, rating).catch(() => {});
  }

  async function handleSwipe(card: DeckCard, dir: SwipeDirection) {
    seenIdsRef.current.add(card.tmdb_id);
    setCards((prev) => prev.filter((c) => c.tmdb_id !== card.tmdb_id));
    try {
      const res = await api.swipe(token, seat, card.tmdb_id, dir);
      if (res.matchFormed) {
        showToast(`✨ It's a match — ${res.matchFormed.title}!`);
      } else if (res.state === "never") {
        showToast("Hidden — you won't see it again", card); // undoable
      } else if (res.state === "watched") {
        promptSeenRating(card);
      }
      await refreshMatches();
    } catch {
      showToast("Couldn't save that swipe");
    }
    if (cards.length <= 3) refill();
  }

  async function undo(card: DeckCard) {
    await api.undo(token, seat, card.tmdb_id).catch(() => {});
    // A "never again" in a battle dropped it from the wishlist — undo restores it.
    if (source === "wishlist") await api.addWishlist(token, seat, card.tmdb_id).catch(() => {});
    setCards((prev) => [card, ...prev]);
    setToast(null);
    refreshMatches();
  }

  const top = cards[0];

  return (
    <>
      {source === "all" && (
        <div className="buffer" data-testid="buffer-status" data-ready={bufferReady ? "true" : "false"}>
          {bufferReady ? (
            <>
              <b data-testid="match-count">{matchCount} matches</b> ready —{" "}
              <a data-testid="see-matches-link" onClick={onGoMatches} style={{ color: "var(--accent)", cursor: "pointer" }}>
                see them →
              </a>
            </>
          ) : (
            <>Building your match buffer… <b data-testid="match-count">{matchCount}</b>/3</>
          )}
        </div>
      )}

      {source === "all" && partnerName && (
        <div
          className="presence-chip"
          data-testid="presence-chip"
          style={{
            margin: "0 auto 10px",
            padding: "5px 12px",
            borderRadius: 999,
            width: "fit-content",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--ink)",
            background: "color-mix(in srgb, var(--accent) 18%, transparent)",
          }}
        >
          💞 {partnerName} is swiping too
        </div>
      )}

      <div className="deck">
        {loading ? (
          <div className="spinner" />
        ) : cards.length === 0 ? (
          <EmptyDeck onRefill={refill} onGoMatches={onGoMatches} hasMatches={matchCount > 0} source={source} />
        ) : (
          cards
            .slice(0, 3)
            .reverse()
            .map((card, i, arr) => {
              const depth = arr.length - 1 - i;
              return (
                <SwipeCard
                  key={card.tmdb_id}
                  card={card}
                  token={token}
                  interactive={card.tmdb_id === top?.tmdb_id}
                  depth={depth}
                  onSwipe={(dir) => handleSwipe(card, dir)}
                  onDetails={onDetails}
                />
              );
            })
        )}
      </div>

      {top && (
        <div className="actions">
          <button data-testid="swipe-later" className="act later" onClick={() => handleSwipe(top, "left")} title="Not right now (swipe left)">
            🕐
          </button>
          <button data-testid="swipe-seen" className="act seen" onClick={() => handleSwipe(top, "down")} title="Seen it (swipe down)">
            👁
          </button>
          <button data-testid="swipe-never" className="act never" onClick={() => handleSwipe(top, "up")} title="Don't want to see it (swipe up)">
            ✕
          </button>
          <button data-testid="swipe-like" className="act like" onClick={() => handleSwipe(top, "right")} title="Like (swipe right)">
            ♥
          </button>
        </div>
      )}

      {seenRate && (
        <div className="seen-rate" data-testid="seen-rate">
          <div className="seen-rate-q">
            Seen <b>{seenRate.title}</b> — how was it?
          </div>
          <div className="seen-rate-row">
            <button data-testid="seen-rate-shit" className="sr-btn sr-shit" onClick={() => rateSeen(seenRate, 1)}>Shit</button>
            <button data-testid="seen-rate-bad" className="sr-btn sr-bad" onClick={() => rateSeen(seenRate, 4)}>bad</button>
            <button data-testid="seen-rate-good" className="sr-btn sr-good" onClick={() => rateSeen(seenRate, 8)}>good</button>
            <button data-testid="seen-rate-idk" className="sr-btn sr-idk" onClick={() => rateSeen(seenRate, null)}>i don't know</button>
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="toast" className="toast">
          {toast.text}
          {toast.undo && <button data-testid="undo-btn" onClick={() => undo(toast.undo!)}>Undo</button>}
        </div>
      )}
    </>
  );
}

function EmptyDeck({
  onRefill,
  onGoMatches,
  hasMatches,
  source,
}: {
  onRefill: () => void;
  onGoMatches: () => void;
  hasMatches: boolean;
  source?: string;
}) {
  if (source === "wishlist") {
    return (
      <div className="center" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 46 }}>⚔️</div>
        <h1>Battle complete!</h1>
        <p className="sub">You've swiped through all wishlist titles. Check your matches!</p>
        {hasMatches && (
          <button className="btn" onClick={onGoMatches}>
            See your matches →
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="center" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 46 }}>🍿</div>
      <h1>That's everything for now</h1>
      <p className="sub">You've been through the deck. {hasMatches ? "Check your matches!" : "Add more services or try again in a bit."}</p>
      {hasMatches ? (
        <button className="btn" onClick={onGoMatches}>
          See your matches →
        </button>
      ) : (
        <button className="btn secondary" onClick={onRefill}>
          Refresh deck
        </button>
      )}
    </div>
  );
}

// ── Seen It / mark watched search ────────────────────────────────────────────
function SeenItScreen({
  token,
  seat,
  onDetails,
}: {
  token: string;
  seat: string;
  onDetails?: (tmdbId: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [markingId, setMarkingId] = useState<number | null>(null);
  const [confirmedIds, setConfirmedIds] = useState<Set<number>>(new Set());
  const [ratingFor, setRatingFor] = useState<number | null>(null);
  const [rating, setRating] = useState(0);
  const [seenList, setSeenList] = useState<SeenEntry[]>([]);
  const [recommended, setRecommended] = useState<DeckCard[]>([]);
  const [alignment, setAlignment] = useState<AlignmentView | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSeen = useCallback(async () => {
    try {
      const { items } = await api.seen(token, seat);
      setSeenList(items);
    } catch {
      // non-fatal
    }
  }, [token, seat]);

  // Load the already-seen list on mount (catches deck "seen it" swipes too).
  useEffect(() => {
    loadSeen();
  }, [loadSeen]);

  // "They loved these" rail + "where you align" — both derive from seen ratings
  // (your own + your partner's), so they live alongside the Seen list.
  useEffect(() => {
    api.deck(token, seat, 12, "recommended").then((r) => setRecommended(r.cards)).catch(() => {});
    api.alignment(token).then(setAlignment).catch(() => {});
  }, [token, seat]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { results } = await api.search(token, query.trim());
        setResults(results);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, token]);

  async function doMarkWatched(tmdbId: number, r: number) {
    setMarkingId(tmdbId);
    try {
      await api.markWatched(token, seat, tmdbId, r > 0 ? r : undefined);
      setConfirmedIds((prev) => new Set(prev).add(tmdbId));
      loadSeen();
    } finally {
      setMarkingId(null);
      setRatingFor(null);
      setRating(0);
    }
  }

  return (
    <div className="scroll">
      <h1>Seen it</h1>
      <p className="sub" style={{ marginBottom: 14 }}>
        Already watched a movie? Mark it so it won't appear in your deck.
      </p>

      <label className="field">
        <span>Search for a title</span>
        <input
          data-testid="seen-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Inception, Parasite…"
        />
      </label>

      {searching && <div className="spinner" style={{ margin: "8px auto" }} />}

      {results.map((r) => (
        <div
          key={r.tmdb_id}
          data-testid="seen-result"
          data-tmdb-id={r.tmdb_id}
          className="matchrow"
        >
          <div
            className="thumb"
            style={{ ...(r.poster ? { backgroundImage: `url(${r.poster})` } : {}), cursor: onDetails ? "pointer" : "default" }}
            onClick={() => onDetails?.(r.tmdb_id)}
          >
            {!r.poster && "🎬"}
          </div>
          <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(r.tmdb_id)}>
            <div className="t">{r.title}</div>
            <div className="m">{[r.year, r.genres.slice(0, 2).join(", ")].filter(Boolean).join(" · ")}</div>
          </div>
          {confirmedIds.has(r.tmdb_id) ? (
            <div style={{ color: "var(--like)", fontSize: 13, fontWeight: 700, padding: "0 8px" }}>✓ Saved</div>
          ) : (
            <button
              data-testid="mark-watched-btn"
              className="btn"
              style={{ width: "auto", padding: "8px 12px", fontSize: 12 }}
              disabled={markingId === r.tmdb_id}
              onClick={() => { setRatingFor(r.tmdb_id); setRating(0); }}
            >
              {markingId === r.tmdb_id ? "…" : "Seen it"}
            </button>
          )}
        </div>
      ))}

      {/* ── "They loved these" — a partner has seen & loved these; you haven't ── */}
      {recommended.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>They loved these 💞</h2>
          <p className="sub" style={{ marginBottom: 10 }}>
            Your partner rated these highly and you haven't seen them. Tap to take a look.
          </p>
          {recommended.map((c) => (
            <div key={c.tmdb_id} data-testid="recommended-item" data-tmdb-id={c.tmdb_id} className="matchrow">
              <div
                className="thumb"
                style={{ ...(c.poster ? { backgroundImage: `url(${c.poster})` } : {}), cursor: onDetails ? "pointer" : "default" }}
                onClick={() => onDetails?.(c.tmdb_id)}
              >
                {!c.poster && "🎬"}
              </div>
              <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(c.tmdb_id)}>
                <div className="t">{c.title}</div>
                <div className="m">{[c.year, c.genres.slice(0, 2).join(", ")].filter(Boolean).join(" · ")}</div>
                <div className="m" style={{ color: "var(--like)" }}>{Math.round(c.predicted_pref * 100)}% match for you</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── "Where you align" — titles you've both seen, bucketed by agreement ── */}
      {alignment && (alignment.both_loved.length + alignment.both_hated.length + alignment.split.length > 0) && (
        <div style={{ marginTop: 18 }} data-testid="alignment">
          <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Where you align</h2>
          {(
            [
              ["both loved", alignment.both_loved, "var(--like)"],
              ["you disagree", alignment.split, "var(--later)"],
              ["both disliked", alignment.both_hated, "var(--never)"],
            ] as const
          ).map(([label, entries, color]) =>
            entries.length === 0 ? null : (
              <div key={label} style={{ marginBottom: 12 }}>
                <div className="m" style={{ color, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {label} ({entries.length})
                </div>
                {entries.map((e) => (
                  <div key={e.tmdb_id} className="matchrow">
                    <div
                      className="thumb"
                      style={{ ...(e.poster ? { backgroundImage: `url(${e.poster})` } : {}), cursor: onDetails ? "pointer" : "default" }}
                      onClick={() => onDetails?.(e.tmdb_id)}
                    >
                      {!e.poster && "🎬"}
                    </div>
                    <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(e.tmdb_id)}>
                      <div className="t">{e.title}</div>
                      <div className="m">{e.ratings.map((r) => `${r.display_name} ${r.rating ?? "–"}`).join("  ·  ")}</div>
                    </div>
                  </div>
                ))}
              </div>
            ),
          )}
        </div>
      )}

      {/* ── Already-seen list ── */}
      <div style={{ marginTop: 18 }}>
        <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Already seen ({seenList.length})</h2>
        {seenList.length === 0 ? (
          <p className="sub">
            Nothing here yet. Mark a title above, or swipe a card down (👁) on the deck — it'll show up here.
          </p>
        ) : (
          seenList.map((s) => (
            <div key={s.tmdb_id} data-testid="seen-item" data-tmdb-id={s.tmdb_id} className="matchrow">
              <div
                className="thumb"
                style={{ ...(s.poster ? { backgroundImage: `url(${s.poster})` } : {}), cursor: onDetails ? "pointer" : "default" }}
                onClick={() => onDetails?.(s.tmdb_id)}
              >
                {!s.poster && "🎬"}
              </div>
              <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(s.tmdb_id)}>
                <div className="t">{s.title}</div>
                <div className="m">{[s.year, s.genres.slice(0, 2).join(", ")].filter(Boolean).join(" · ")}</div>
                {s.rating != null && (
                  <div className="m" style={{ color: "var(--later)" }}>★ {s.rating}/10</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Rating modal for seen-it ── */}
      {ratingFor !== null && (
        <div className="rating-modal-bg" onClick={() => setRatingFor(null)}>
          <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Rate it (optional)</h3>
            <div className="stars-row">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <span
                  key={n}
                  className={n <= rating ? "on" : ""}
                  onClick={() => setRating(n)}
                  role="button"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  ★
                </span>
              ))}
            </div>
            <button
              className="btn"
              disabled={markingId === ratingFor}
              onClick={() => doMarkWatched(ratingFor, rating)}
            >
              {markingId === ratingFor ? "Saving…" : "Mark as watched"}
            </button>
            <div style={{ height: 8 }} />
            <button className="btn ghost" onClick={() => setRatingFor(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Matches (§2 step 7) ──────────────────────────────────────────────────────
function MatchesScreen({ token, coupleId, seat: _seat, onChoose, onDetails }: { token: string; coupleId: string; seat: string; onChoose: () => void; onDetails?: (tmdbId: number) => void }) {
  const [matches, setMatches] = useState<MatchView[]>([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<number | null>(null);

  useEffect(() => {
    api.matches(token).then((m) => {
      setMatches(m.matches);
      setReady(m.ready);
      setLoading(false);
    });
  }, [token]);

  async function choose(m: MatchView) {
    setPicked(m.tmdb_id);
    // A fresh pick should prompt for feedback next open — forget any earlier
    // "already shown" mark so the post-watch question can appear again.
    clearFeedbackSeen(coupleId);
    await api.choose(token, m.tmdb_id);
    onChoose();
  }

  if (loading) return <div className="center"><div className="spinner" /></div>;

  return (
    <div className="scroll">
      <h1>Your matches</h1>
      {!ready && (
        <div data-testid="matches-not-ready" className="banner">Keep swiping — we surface matches once at least 3 are ready ({matches.length}/3).</div>
      )}
      {matches.length === 0 && <p className="sub">No mutual matches yet. Head back and swipe a few more.</p>}
      {matches.map((m) => (
        <div data-testid="match-row" className="matchrow" key={m.tmdb_id}>
          <div
            className="thumb"
            style={m.poster ? { backgroundImage: `url(${m.poster})`, cursor: onDetails ? "pointer" : "default" } : { cursor: onDetails ? "pointer" : "default" }}
            onClick={() => onDetails?.(m.tmdb_id)}
            aria-label={onDetails ? `Open details for ${m.title}` : undefined}
          >
            {!m.poster && "🎬"}
          </div>
          <div className="body" style={{ cursor: onDetails ? "pointer" : "default" }} onClick={() => onDetails?.(m.tmdb_id)}>
            <div className="t" data-testid="match-title">{m.title}</div>
            <div className="m">
              {[m.year, m.runtime ? `${m.runtime}m` : null, m.genres.slice(0, 2).join(", ")].filter(Boolean).join(" · ")}
            </div>
            <div className="chips">
              {m.available_on.map((o) => (
                <span data-testid="match-availability-chip" className="chip svc" key={o.service}>
                  {o.name}
                </span>
              ))}
            </div>
          </div>
          <button data-testid="match-watch-btn" className="btn" style={{ width: "auto", padding: "10px 14px" }} disabled={picked === m.tmdb_id} onClick={() => choose(m)}>
            {picked === m.tmdb_id ? "Enjoy! 🎉" : "Watch"}
          </button>
        </div>
      ))}
      <p data-testid="justwatch-attribution" className="attribution">{JUSTWATCH_ATTRIBUTION}</p>
    </div>
  );
}

// ── Post-watch feedback (§6.6) ───────────────────────────────────────────────
function FeedbackScreen({
  token,
  pending,
  seat,
  onResolved,
}: {
  token: string;
  pending: PendingFeedback;
  seat: string;
  onResolved: () => void;
}) {
  const [rating, setRating] = useState(0);
  const [mode, setMode] = useState<"ask" | "rate">("ask");

  async function submit(outcome: "watched" | "abandoned" | "not_yet") {
    const ratings = outcome === "watched" && rating > 0 ? { [seat]: rating } : undefined;
    await api.feedback(token, pending.tmdb_id, outcome, ratings);
    onResolved();
  }

  return (
    <div className="center" style={{ textAlign: "center" }}>
      <div className="thumb" style={{ ...(pending.poster ? { backgroundImage: `url(${pending.poster})` } : {}), width: 120, height: 170, borderRadius: 16, margin: "0 auto", backgroundSize: "cover", backgroundColor: "#2d2750", display: "grid", placeItems: "center", fontSize: 40 }}>
        {!pending.poster && "🎬"}
      </div>
      <h1>Did you two watch {pending.title}?</h1>
      {mode === "ask" ? (
        <div>
          <button data-testid="feedback-yes" className="btn" onClick={() => setMode("rate")}>
            Yes — rate it
          </button>
          <div style={{ height: 10 }} />
          <button data-testid="feedback-abandoned" className="btn secondary" onClick={() => submit("abandoned")}>
            We started but didn't finish
          </button>
          <div style={{ height: 10 }} />
          <button data-testid="feedback-not-yet" className="btn ghost" onClick={() => submit("not_yet")}>
            Not yet
          </button>
        </div>
      ) : (
        <div>
          <p className="sub">Your rating (folds into your taste — your partner rates on their device):</p>
          <div className="rating-row" style={{ justifyContent: "center" }}>
            <div data-testid="rating-stars" className="stars">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <span key={n} data-testid={`star-${n}`} className={n <= rating ? "on" : ""} onClick={() => setRating(n)} style={{ cursor: "pointer" }}>
                  ★
                </span>
              ))}
            </div>
          </div>
          <button data-testid="save-rating-btn" className="btn" disabled={rating === 0} onClick={() => submit("watched")}>
            Save rating
          </button>
        </div>
      )}
    </div>
  );
}

// ── ShareInline — reusable code + share/copy button ─────────────────────────
function ShareInline({ couple }: { token: string; couple: CoupleView }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator.share === "function";
  async function share() {
    const url = couple.share_url;
    if (canShare) {
      // Do NOT pass a separate `text` field alongside `url`: some share targets
      // (iOS Messages, some Android apps) concatenate them, corrupting the link.
      await navigator.share({ title: "hygge밤", url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }
  return (
    <div>
      <div data-testid="couple-code" className="code">{couple.short_code}</div>
      <p className="sub" style={{ textAlign: "center", margin: "8px 0 12px" }}>
        Your couple code
      </p>
      <button data-testid="copy-share-btn" className="btn" onClick={share}>
        {copied ? "Copied ✓" : canShare ? "Share link" : "Copy link"}
      </button>
    </div>
  );
}

function NotFound() {
  return (
    <div data-testid="not-found" className="center" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 46 }}>🔗</div>
      <h1>This link isn't working</h1>
      <p className="sub">It may have been regenerated. Ask your partner to re-share their link, or start fresh.</p>
      <button data-testid="create-new-couple-btn" className="btn" onClick={() => { clearLastCouple(); navigate("/"); }}>
        Create a new couple
      </button>
    </div>
  );
}
